import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../components/common/notification/Notification";
import { matchesSearch } from "../utils/response";

const BILLING_DEBUG = process.env.NODE_ENV !== "production";

const normalizeCollectionResult = (response) => {
  const items = Array.isArray(response?.items) ? response.items : [];
  const totalPages = Number(response?.totalPages);

  return {
    items,
    totalPages:
      Number.isFinite(totalPages) && totalPages > 0
        ? totalPages
        : items.length
          ? 1
          : 0,
  };
};

/**
 * Reusable CRUD manager for billing collections like subscription plans and coupons.
 */
export const useBillingCrudManager = ({
  entityLabel,
  loadList,
  loadEditableItem,
  createItem,
  updateItem,
  deleteItem,
  toggleItemStatus,
  searchFields = [],
  defaultFilters = {},
  getItemId = (item) => item?.id,
  getItemStatus = (item) => Boolean(item?.isActive),
  setItemStatus = (item, nextStatus) => ({
    ...item,
    isActive: nextStatus,
    active: nextStatus,
  }),
  toFormValues = (item) => item,
  toPayload = (values) => values,
  pageSize = 10,
}) => {
  const isMountedRef = useRef(false);
  const latestRequestRef = useRef(0);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formState, setFormState] = useState({
    isOpen: false,
    mode: "create",
    item: null,
    values: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchRecords = useCallback(
    async (overrideState = {}) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      if (isMountedRef.current) {
        setIsLoading(true);
        setErrorMessage("");
      }

      const nextPage = overrideState.page ?? page;
      const nextFilters = overrideState.filters ?? filters;

      try {
        if (BILLING_DEBUG) {
          console.info(`[billing:${entityLabel}] fetch start`, {
            page: nextPage,
            pageSize,
            filters: nextFilters,
          });
        }

        const response = await loadList({
          page: nextPage,
          pageSize,
          filters: nextFilters,
        });
        const normalizedResponse = normalizeCollectionResult(response);

        if (BILLING_DEBUG) {
          console.info(`[billing:${entityLabel}] fetch success`, {
            items: normalizedResponse.items.length,
            totalPages: normalizedResponse.totalPages,
          });
        }

        if (!isMountedRef.current || latestRequestRef.current !== requestId) {
          return;
        }

        setRecords(normalizedResponse.items);
        setTotalPages(normalizedResponse.totalPages);
      } catch (error) {
        if (!isMountedRef.current || latestRequestRef.current !== requestId) {
          return;
        }

        const message =
          error?.message ||
          error?.response?.data?.message ||
          `Unable to load ${entityLabel.toLowerCase()}s right now.`;

        if (BILLING_DEBUG) {
          console.error(`[billing:${entityLabel}] fetch failed`, message, error);
        }

        setErrorMessage(message);
        setRecords([]);
        setTotalPages(0);
      } finally {
        if (isMountedRef.current && latestRequestRef.current === requestId) {
          if (BILLING_DEBUG) {
            console.info(`[billing:${entityLabel}] fetch end`);
          }
          setIsLoading(false);
        }
      }
    },
    [entityLabel, filters, loadList, page, pageSize]
  );

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filteredRecords = useMemo(
    () => records.filter((record) => matchesSearch(record, searchValue, searchFields)),
    [records, searchFields, searchValue]
  );

  const openCreateForm = useCallback(() => {
    setFormState({
      isOpen: true,
      mode: "create",
      item: null,
      values: toFormValues(null),
    });
  }, [toFormValues]);

  const openEditForm = useCallback(
    async (itemOrId) => {
      setIsSubmitting(true);

      try {
        const editableItem = loadEditableItem
          ? await loadEditableItem(itemOrId)
          : itemOrId;

        setFormState({
          isOpen: true,
          mode: "edit",
          item: editableItem,
          values: toFormValues(editableItem),
        });
      } catch (error) {
        showToast(
          error?.message ||
            `Unable to load ${entityLabel.toLowerCase()} details.`,
          {
            type: "error",
          }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [entityLabel, loadEditableItem, toFormValues]
  );

  const closeForm = useCallback(() => {
    setFormState({
      isOpen: false,
      mode: "create",
      item: null,
      values: null,
    });
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);

      try {
        const payload = toPayload(values, formState.item, formState.mode);

        if (formState.mode === "edit") {
          await updateItem(payload, formState.item);
        } else {
          await createItem(payload);
        }

        showToast(
          `${entityLabel} ${formState.mode === "edit" ? "updated" : "created"} successfully.`,
          { type: "success" }
        );

        closeForm();
        await fetchRecords();
        return true;
      } catch (error) {
        const fallbackMessage =
          formState.mode === "edit"
            ? `Unable to update ${entityLabel.toLowerCase()}.`
            : `Unable to create ${entityLabel.toLowerCase()}.`;

        showToast(error?.message || error?.response?.data?.message || fallbackMessage, {
          type: "error",
        });

        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      closeForm,
      createItem,
      entityLabel,
      fetchRecords,
      formState.item,
      formState.mode,
      toPayload,
      updateItem,
    ]
  );

  const askToDelete = useCallback((item) => {
    setDeleteTarget(item);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      return false;
    }

    setIsSubmitting(true);

    try {
      await deleteItem(getItemId(deleteTarget));
      await fetchRecords();
      showToast(`${entityLabel} deleted successfully.`, {
        type: "success",
      });
      setDeleteTarget(null);
      return true;
    } catch (error) {
      showToast(
        error?.message ||
        error?.response?.data?.message ||
          `Unable to delete ${entityLabel.toLowerCase()}.`,
        {
          type: "error",
        }
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteItem, deleteTarget, entityLabel, fetchRecords, getItemId]);

  const handleStatusToggle = useCallback(
    async (item, nextStatus) => {
      const itemId = getItemId(item);
      const previousStatus = getItemStatus(item);

      setRecords((currentRecords) =>
        currentRecords.map((record) =>
          getItemId(record) === itemId ? setItemStatus(record, nextStatus) : record
        )
      );

      try {
        await toggleItemStatus(itemId, nextStatus);
        showToast(
          `${entityLabel} ${nextStatus ? "activated" : "deactivated"} successfully.`,
          {
            type: "success",
          }
        );
      } catch (error) {
        setRecords((currentRecords) =>
          currentRecords.map((record) =>
            getItemId(record) === itemId
              ? setItemStatus(record, previousStatus)
              : record
          )
        );

        showToast(
          error?.message ||
          error?.response?.data?.message ||
            `Unable to update ${entityLabel.toLowerCase()} status.`,
          {
            type: "error",
          }
        );
      }
    },
    [
      entityLabel,
      getItemId,
      getItemStatus,
      setItemStatus,
      toggleItemStatus,
    ]
  );

  const updateFilters = useCallback((updater) => {
    setPage(1);
    setFilters((currentFilters) =>
      typeof updater === "function"
        ? updater(currentFilters)
        : { ...currentFilters, ...updater }
    );
  }, []);

  const refresh = useCallback(async () => {
    await fetchRecords();
  }, [fetchRecords]);

  return {
    records,
    filteredRecords,
    page,
    pageSize,
    totalPages,
    filters,
    searchValue,
    isLoading,
    isSubmitting,
    errorMessage,
    formState,
    deleteTarget,
    setPage,
    setSearchValue,
    updateFilters,
    refresh,
    openCreateForm,
    openEditForm,
    closeForm,
    handleSubmit,
    askToDelete,
    cancelDelete,
    confirmDelete,
    handleStatusToggle,
  };
};
